import React, { useState } from 'react';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import SelectionHeader from 'talent_matching/components/header_row/selection_header';
import AdvanceButton from 'talent_matching/components/bulk_actions/advance/advance_button';
import AdvanceModal from 'talent_matching/components/bulk_actions/advance/advance_modal';
import RejectButton from 'talent_matching/components/bulk_actions/reject/reject_button';
import BulkRejectionModal from 'talent_matching/components/bulk_actions/reject/bulk_rejection_modal';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import {
  SecondaryButton,
  SecondaryButtonLink,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { GridApi, GridColumnVisibilityModel } from '@mui/x-data-grid';
import BulkActionsMenu from 'talent_matching/components/bulk_actions/menu';
import SendEmailModal from 'talent_matching/components/bulk_actions/send_email/send_email_modal';
import AddTagsModal from 'talent_matching/components/bulk_actions/add_tags_modal';
import FeedbackModal from 'talent_matching/components/bulk_actions/feedback/feedback_modal';
import FeedbackButton from 'talent_matching/components/bulk_actions/feedback/feedback_button';
import FraudReportModal from 'talent_matching/components/bulk_actions/fraud_report/fraud_report_modal';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { standardRequest } from 'shared/utils/request';
import CustomColumnSelector from 'talent_matching/components/shared/custom_column_selector';

const t = withLocalePrefix('talent_matching.header_row');

type Props = {
  selectionsHelper: SelectionsHelper;
  apiRef: React.MutableRefObject<GridApi>;
  columnSelectorColumns: { field: string; label: string }[];
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
};

const HeaderRow = ({
  selectionsHelper,
  apiRef,
  columnSelectorColumns,
  columnVisibilityModel,
  setColumnVisibilityModel,
  defaultColumnVisibilityModel,
}: Props) => {
  const { canExport } = useUserContext();
  const {
    paths,
    canAdvance,
    canBulkEdit,
    feedbackOnly,
    canReject,
    canEmail,
    canRunFraudReports,
    canViewTalentMatchingAiMatchScore,
  } = useTalentMatchingTabContext();
  const [loading, setLoading] = useState(false);
  const [rejectAsSpam, setRejectAsSpam] = useState(false);

  const [rejectIsOpen, openRejectModal, closeRejectModal] = useModal();
  const [advanceIsOpen, openAdvanceModal, closeAdvanceModal] = useModal();
  const [sendEmailIsOpen, openSendEmailModal, closeSendEmailModal] = useModal();
  const [addTagsIsOpen, openAddTagsModal, closeAddTagsModal] = useModal();
  const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] = useModal();
  const [fraudReportIsOpen, openFraudReportModal, closeFraudReportModal] =
    useModal();

  const onOpenRejectModal = (asSpam = false) => {
    setLoading(true);
    setRejectAsSpam(asSpam);
    openRejectModal();
  };

  const onCloseRejectModal = () => {
    closeRejectModal();
    setRejectAsSpam(false);
  };

  const openInAppReview = () => {
    const appIds = selectionsHelper.applicationIds;
    const baseUrl = new URL(paths.appReviewPath, window.location.origin);
    appIds.forEach((id) =>
      baseUrl.searchParams.append('application_ids[]', id.toString())
    );
    if (canViewTalentMatchingAiMatchScore) {
      baseUrl.searchParams.append('show_match_score', 'true');
    }
    window.open(baseUrl.toString(), '_blank');
  };

  const saveAllCalibrations = async () => {
    setLoading(true);
    await standardRequest.post(paths.saveAllCalibrationsPath);
    setLoading(false);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={3} justifyContent="flex-start">
          <SelectionHeader
            selectionsHelper={selectionsHelper}
            apiRef={apiRef}
          />
        </Stack>

        {selectionsHelper.noneSelected ? (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
          >
            <CustomColumnSelector
              columns={columnSelectorColumns}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              defaultColumnVisibilityModel={defaultColumnVisibilityModel}
            />
            {canExport && (
              <Stack direction="row" spacing={2}>
                <Grid>
                  <SecondaryButton
                    onClick={saveAllCalibrations}
                    disabled={loading}
                  >
                    {t('buttons.save_all_calibrations')}
                  </SecondaryButton>
                </Grid>
                <Grid>
                  <SecondaryButtonLink href={paths.exportScoresPath}>
                    {t('buttons.export')}
                  </SecondaryButtonLink>
                </Grid>
              </Stack>
            )}
          </Stack>
        ) : (
          canBulkEdit &&
          (feedbackOnly ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <FeedbackButton
                openModal={openFeedbackModal}
                selectionsHelper={selectionsHelper}
                loading={loading}
              />
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <FeedbackButton
                openModal={openFeedbackModal}
                selectionsHelper={selectionsHelper}
                loading={loading}
              />
              <AdvanceButton
                openModal={openAdvanceModal}
                selectionsHelper={selectionsHelper}
                loading={loading}
                canAdvance={canAdvance}
              />
              <RejectButton
                openModal={() => onOpenRejectModal(false)}
                selectionsHelper={selectionsHelper}
                loading={loading}
                canReject={canReject}
              />
              <BulkActionsMenu
                openSendEmailModal={openSendEmailModal}
                openAddTagsModal={openAddTagsModal}
                openRejectModal={() => onOpenRejectModal(true)}
                openFraudReportModal={openFraudReportModal}
                canReject={canReject}
                canEmail={canEmail}
                canRunFraudReports={canRunFraudReports}
                openInAppReview={openInAppReview}
              />
            </Stack>
          ))
        )}
      </Stack>

      {/* This forces the modal to reset when the open state changes */}
      {rejectIsOpen && (
        <BulkRejectionModal
          isOpen={rejectIsOpen}
          closeModal={onCloseRejectModal}
          selectionsHelper={selectionsHelper}
          loadingComplete={() => setLoading(false)}
          asSpam={rejectAsSpam}
        />
      )}
      {paths.advanceStagePath && (
        <AdvanceModal
          isOpen={advanceIsOpen}
          closeModal={closeAdvanceModal}
          selectionsHelper={selectionsHelper}
          advanceStagePath={paths.advanceStagePath}
        />
      )}
      {sendEmailIsOpen && (
        <SendEmailModal
          isOpen={sendEmailIsOpen}
          closeModal={closeSendEmailModal}
          selectionsHelper={selectionsHelper}
          loadingComplete={() => setLoading(false)}
        />
      )}
      {addTagsIsOpen && (
        <AddTagsModal
          isOpen={addTagsIsOpen}
          closeModal={closeAddTagsModal}
          selectionsHelper={selectionsHelper}
        />
      )}
      <FeedbackModal
        isOpen={showFeedbackModal}
        closeModal={closeFeedbackModal}
        selectionsHelper={selectionsHelper}
      />
      {fraudReportIsOpen && (
        <FraudReportModal
          isOpen={fraudReportIsOpen}
          closeModal={closeFraudReportModal}
          selectionsHelper={selectionsHelper}
        />
      )}
    </>
  );
};

export default HeaderRow;
